// One order's report: load it, apply a template to it, save it, finalize it.
//
// The rule this hook exists to enforce: applying a template COPIES its HTML
// into the report. From that moment the report owns its text — later edits to
// (or deletion of) the source template can never change it. template_id is
// kept only so the UI can say where the text originally came from.
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchReport, saveReport } from "../api/radiologyReportApi";
import {
  hydratePlaceholders,
  htmlIsEmpty,
  placeholderValues,
} from "../utils/radiologyPlaceholders";

// "Unsaved" must mean the radiologist actually changed something — not that
// the editor rewrote its own markup. TipTap normalises an empty document to
// "<p></p>" on focus, and prosemirror-tables' fixTables pass adds colgroup /
// colwidth / colspan attributes to any table the moment it loads. Both fire the
// change handler without a single keystroke, which is why a freshly-finalized
// report used to show "Unsaved".
//
// So instead of a flag that any change sets, dirtiness is derived: compare a
// signature of the current report against the last saved/loaded one, with the
// editor's own cosmetic rewrites stripped out.
const contentSignature = (html) =>
  String(html || "")
    .replace(/<colgroup>[\s\S]*?<\/colgroup>/gi, "")
    .replace(/\s(?:colwidth|colspan|rowspan)="[^"]*"/gi, "")
    .replace(/<p><\/p>/gi, "")
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .replace(/>\s+</g, "><")
    .trim();

const reportSignature = (r) =>
  `${contentSignature(r?.content_html)}|${String(r?.radiologist_name || "").trim()}`;

const emptyReport = (order, patient) => ({
  order_no: order?.orderNo || "",
  admission_id: patient?.admissionId ?? null,
  template_id: null,
  template_name: "",
  content_html: "",
  status: "draft",
  radiologist_name: sessionStorage.getItem("doctor_name") || "",
  reported_at: null,
});

export default function useRadiologyReport({ patient, order, initialReport }) {
  const [report, setReport] = useState(
    () => initialReport || emptyReport(order, patient),
  );
  const [loading, setLoading] = useState(!initialReport);
  const [saving, setSaving] = useState(false);
  // Signature of the report as last saved (or as loaded). Anything differing
  // from this is a genuine unsaved edit.
  const [savedSignature, setSavedSignature] = useState(() =>
    reportSignature(initialReport || emptyReport(order, patient)),
  );
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A report passed in from the orders list is authoritative (it is the same
  // object the list holds); only go to the API when we weren't given one.
  useEffect(() => {
    if (initialReport || !order?.orderNo) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const found = await fetchReport(order.orderNo);
        if (!cancelled && found && mounted.current) {
          setReport(found);
          setSavedSignature(reportSignature(found));
        }
      } catch (e) {
        // A missing report is the normal case for a new study — not an error.
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order?.orderNo, initialReport]);

  const setContent = useCallback((html) => {
    setReport((prev) => (prev.content_html === html ? prev : { ...prev, content_html: html }));
  }, []);

  const setRadiologist = useCallback((name) => {
    setReport((prev) => ({ ...prev, radiologist_name: name }));
  }, []);

  // Copy the template's HTML in, resolving {{placeholders}} against this
  // patient/order. The template object itself is never retained — only its id
  // and name, for provenance.
  //
  // Returns the hydrated HTML so the caller can push it straight into the
  // editor instance: ClinicalEditor ignores incoming `value` while it has
  // focus (to protect the caret), and clicking a button does not blur it in
  // every browser, so relying on the prop alone would drop the load.
  const applyTemplate = useCallback(
    (template) => {
      if (!template) return "";
      const values = placeholderValues(patient, order, {
        radiologistName: report.radiologist_name,
      });
      const html = hydratePlaceholders(template.content_html, values);
      setReport((prev) => ({
        ...prev,
        template_id: template.template_id,
        template_name: template.template_name,
        content_html: html,
      }));
      return html;
    },
    [patient, order, report.radiologist_name],
  );

  const persist = useCallback(
    async (status) => {
      setSaving(true);
      try {
        const payload = {
          ...report,
          order_no: order?.orderNo || report.order_no,
          admission_id: patient?.admissionId ?? report.admission_id,
          status,
          reported_at: status === "final" ? report.reported_at || new Date().toISOString() : null,
        };
        const saved = await saveReport(payload);
        if (mounted.current) {
          setReport(saved);
          setSavedSignature(reportSignature(saved));
        }
        return saved;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [report, order, patient],
  );

  const saveDraft = useCallback(() => persist("draft"), [persist]);
  const finalize = useCallback(() => persist("final"), [persist]);

  // Reverts a finalized report to draft for correction. The backend should
  // record this as an amendment; the UI keeps reported_at until re-finalized.
  // Not an edit in itself — "Unsaved" only appears once the text actually changes.
  const reopen = useCallback(() => {
    setReport((prev) => ({ ...prev, status: "draft" }));
  }, []);

  return {
    report,
    loading,
    saving,
    dirty: reportSignature(report) !== savedSignature,
    isEmpty: htmlIsEmpty(report.content_html),
    isFinal: report.status === "final",
    setContent,
    setRadiologist,
    applyTemplate,
    saveDraft,
    finalize,
    reopen,
  };
}
